import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import CurrencyContext from 'contexts/currencyContext'
import InlineIcon from 'components/utils/InlineIcon'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useNavigateTo from 'hooks/useNavigateTo'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useLuxPlusSubscriptionProduct from 'luxPlus/hooks/useLuxPlusSubscriptionProduct'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'

interface Props {
  onCheckAvailability?: () => void
}

function MaldivesReturnForFreeInfoModal({ onCheckAvailability }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const navigateTo = useNavigateTo()
  const currencyCode = useContext(CurrencyContext)
  const isMember = useAppSelector(getCanRedeemLuxPlusBenefits)

  // Non-members are quoted the subscription price, so the product needs fetching.
  useLuxPlusSubscriptionProduct(!isMember)
  const subscriptionPrice = useAppSelector(state => state.luxPlus.products.subscription.offers?.[0]?.price)

  // The yearly price only appears once the subscription product has loaded, so the copy
  // has a variant that reads correctly without it.
  const nonMemberBenefitCopy = subscriptionPrice === undefined
    ? intl.formatMessage({
        id: 'MSIgxDdvRv70kZZtGghz',
        defaultMessage: "Free return flights are a LuxPlus+ benefit. Join and you'll unlock this saving, plus member pricing on every escape after it.",
      })
    : intl.formatMessage({
        id: '7ACNw-8vsfyGnzeCzLrx',
        defaultMessage: "Free return flights are a LuxPlus+ benefit. Join for {price} a year and you'll unlock this saving, plus member pricing on every escape after it.",
      }, { price: intl.formatNumber(subscriptionPrice, { style: 'currency', currency: currencyCode, maximumFractionDigits: 0 }) })

  const handleCheckAvailability = useCallback(() => {
    modalElement?.resolve()
    onCheckAvailability?.()
  }, [modalElement, onCheckAvailability])

  const handleJoin = useCallback(() => {
    modalElement?.resolve()
    navigateTo(`/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`)
  }, [modalElement, navigateTo])

  return (
    <ModalBase size="S" mode="default" dismissible>
      <ModalHeader
        title={intl.formatMessage({
          id: '2O8wtNIfhYpbbTdhpFCg',
          defaultMessage: 'Maldives return for free flight sale',
        })}
      />
      <ModalBody>
        <Sheet background="normal-background" padding={24}>
          <Group direction="vertical" gap={20}>
            <Group direction="vertical" gap={4}>
              <Heading variant="heading6" as="h3">
                {intl.formatMessage({
                  id: 'BwpDBCo_TwVivwv6isHI',
                  defaultMessage: 'How it works',
                })}
              </Heading>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'VQIC7-6rDkfEHVnRl7Ru',
                  defaultMessage: "Book a Maldives hotel and flights package on an eligible date and you'll pay nothing for the flight home. The saving's already included in the price you see.",
                })}
              </BodyText>
            </Group>

            <Group direction="vertical" gap={4}>
              <Heading variant="heading6" as="h3">
                <FormattedMessage
                  id="iVJS1x118esGI9VGNTlQ"
                  defaultMessage="Where to find free return flights {flame}"
                  
                  values={{ flame: <InlineIcon Icon={SolidFireIcon} colour="lux-plus" /> }}
                />
              </Heading>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: '7BqkplpLz6K8GIsmVrO_',
                  defaultMessage: 'Look for the blue flame in the calendar. It marks the months and dates where free return seats are still available.',
                })}
              </BodyText>
            </Group>

            <Group direction="vertical" gap={4}>
              <Heading variant="heading6" as="h3">
                {isMember
                  ? intl.formatMessage({
                      id: 'dacn6OoP8hZt03ORYPL4',
                      defaultMessage: 'LuxPlus+ member exclusive',
                    })
                  : intl.formatMessage({
                      id: 'okOqkjqyoNbBJBSfQG7F',
                      defaultMessage: 'Exclusive to LuxPlus+ members',
                    })}
              </Heading>
              {isMember && <BodyText variant="medium">
                {intl.formatMessage({
                  id: '1G3JNgEKDTHwrchRKAG8',
                  defaultMessage: "You're a LuxPlus+ member, so the free return flight is built into every eligible price you see. Nothing extra to do at checkout.",
                })}
              </BodyText>}
              {!isMember && <>
                <BodyText variant="medium">{nonMemberBenefitCopy}</BodyText>
                <div>
                  <TextLink onClick={handleJoin} weight="bold">
                    {intl.formatMessage({
                      id: 'oiklmuc-ZQWLnJZ7Qzh2',
                      defaultMessage: 'Join LuxPlus+',
                    })}
                  </TextLink>
                </div>
              </>}
            </Group>

            <div>
              <TextLink to="/promotions" weight="regular">
                {intl.formatMessage({
                  id: 'tmYQbH2WPsVfYvPyprP3',
                  defaultMessage: 'T&Cs apply',
                })}
              </TextLink>
            </div>
          </Group>
        </Sheet>
      </ModalBody>

      {!!onCheckAvailability && <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'WsDBcY5s-HF6Qd5_dOPz',
            defaultMessage: 'Check availability',
          }),
          onClick: handleCheckAvailability,
        }}
      />}
    </ModalBase>
  )
}

export default MaldivesReturnForFreeInfoModal
